/**
 * The iPhone/iPad heads-up before the photo picker (docs/design/HANDOFF.md,
 * "iPhone heads-up before the photo picker", `PopIphone`).
 *
 * After the blue check mark, iOS converts every picked photo to JPEG one at a
 * time with the picker still on screen, and tells the page nothing until all
 * of them are done (WebKit WKFileUploadPanel). With 20 photos the picker looks
 * frozen, so Page photos explains the wait before opening it.
 *
 * Shown until photos have arrived from the picker once in this browser: a
 * customer who backs out, or gives up during the wait and closes the tab,
 * sees it again next time.
 */

const DONE_KEY = 'md:photoPickerHeadsUpDone';

/**
 * 'iphone', 'ipad' or null. iPad Safari (and Chrome on iPad) report a Mac
 * user agent; a touch screen gives them away (Macs report 0 touch points).
 */
export function applePickerDevice(nav = typeof navigator === 'undefined' ? null : navigator) {
  if (!nav) return null;
  const ua = nav.userAgent || '';
  if (/iPhone|iPod/.test(ua)) return 'iphone';
  if (/iPad/.test(ua)) return 'ipad';
  if (/Macintosh/.test(ua) && nav.maxTouchPoints > 1) return 'ipad';
  return null;
}

/** True once photos have arrived from the picker in this browser. Unreadable storage counts as "not yet". */
export function pickerHeadsUpDone() {
  try {
    return window.localStorage.getItem(DONE_KEY) === '1';
  } catch (_) {
    return false;
  }
}

export function markPickerHeadsUpDone() {
  try {
    window.localStorage.setItem(DONE_KEY, '1');
  } catch (_) {
    /* private browsing or storage blocked: they'll see it again, which is fine */
  }
}

/** Whether opening the picker on Page photos should show the heads-up first. */
export function shouldShowPickerHeadsUp(nav) {
  return !!applePickerDevice(nav) && !pickerHeadsUpDone();
}
